// SPDX-License-Identifier: GPL-3.0-or-later
//
// Thin wrapper around the wallet's AI calls. Centralises model selection
// (quick vs. in-depth) and one piece of resilience: when the wallet has
// auto-locked itself between calls, attempt a single silent reconnect (which
// triggers the wallet's unlock prompt) and retry the request once.
//
// We call `completeStream()` rather than `complete()` because the SDK's
// non-streaming RPC has a hardcoded 30s timeout, which is too tight for
// longer generations (e.g. interview prep at 3000 maxTokens). The wallet
// currently emits the response as a single content chunk plus a `done` chunk
// rather than streaming token-by-token, but the streaming RPC's per-chunk
// timeout is what we care about — callers don't consume tokens incrementally.

import { getManifest } from '../store/applications';
import { type ModelChoice } from '../types';
import { isWalletLockedError, reconnectWallet, requireConnection } from '../wallet';

export const QUICK_DEFAULT: ModelChoice = { provider: 'anthropic', id: 'claude-haiku-4-5' };
export const IN_DEPTH_DEFAULT: ModelChoice = { provider: 'anthropic', id: 'claude-sonnet-4-6' };

export function quickModel(): ModelChoice {
  return getManifest()?.config.quickModel ?? QUICK_DEFAULT;
}

export function inDepthModel(): ModelChoice {
  return getManifest()?.config.inDepthModel ?? IN_DEPTH_DEFAULT;
}

export interface CompleteOptions {
  system: string;
  user: string;
  maxTokens: number;
  /** Which configured model to use. Defaults to 'in-depth'. */
  kind?: 'quick' | 'in-depth';
  temperature?: number;
}

export interface CompleteResult {
  content: string;
  model: string | undefined;
  provider: string | undefined;
}

async function callOnce(opts: CompleteOptions): Promise<CompleteResult> {
  const conn = requireConnection();
  const m = opts.kind === 'quick' ? quickModel() : inDepthModel();
  const iter = conn.completeStream({
    messages: [
      { role: 'system', content: opts.system },
      { role: 'user', content: opts.user },
    ],
    provider: m.provider,
    model: m.id,
    maxTokens: opts.maxTokens,
    ...(opts.temperature !== undefined ? { temperature: opts.temperature } : {}),
  });
  let content = '';
  for await (const chunk of iter) {
    const piece = (chunk as { content?: string }).content;
    if (typeof piece === 'string') content += piece;
  }
  return { content, model: m.id, provider: m.provider };
}

let inFlight = 0;
const busyListeners = new Set<() => void>();

export function isAiBusy(): boolean {
  return inFlight > 0;
}

export function onAiBusyChange(fn: () => void): () => void {
  busyListeners.add(fn);
  return () => busyListeners.delete(fn);
}

function emitBusy(): void {
  for (const fn of busyListeners) fn();
}

export async function complete(opts: CompleteOptions): Promise<CompleteResult> {
  inFlight++;
  emitBusy();
  try {
    return await callOnce(opts);
  } catch (err) {
    if (!isWalletLockedError(err)) throw err;
    // Wallet auto-locked between calls. Trigger the wallet UI to unlock and
    // retry the request once with the fresh connection.
    const ok = await reconnectWallet();
    if (!ok) throw err;
    return await callOnce(opts);
  } finally {
    inFlight--;
    emitBusy();
  }
}
